/* eslint-disable no-unused-expressions */
import React, { useState, useRef } from 'react'
import i18n from 'live/services/i18n'
import styled from 'styled-components'
import { lighten } from 'polished'
import { WidgetEditorContext } from 'live/context/widget-editor'
import { ChannelConfigs } from './channel-configs/ChannelConfigs'
import { cloneDeepGridEditorChannel } from '../../../../../cloneDeepFunctions'

export interface SelectedChannelProps {
    channelOption: GridEditorChannelOption
    channelsLiteral: AllChannelsMap
    onRemove: (channelOption) => void
    onChangeGroupChannelConfig: (mnemonic: string, configKey: string, newValue: string | boolean | AlarmsProps) => void
    syncLockedChannelsConfig: (option, value, field) => void
    getChannelLockState: (opt: LiveSelectChannelOption) => boolean
    onLock: (opt: LiveSelectChannelOption, state: boolean) => void
    widgetConfig?: GridConfig
    onChangeGroup: (group: GridEditorGroup, updateQuery: boolean) => void
}

export default function SelectedChannelConfig(props: SelectedChannelProps): JSX.Element {
    const channelOption = cloneDeepGridEditorChannel(props?.channelOption ?? {})
    const [onRemove, onChangeGroupChannelConfig] = [props.onRemove, props.onChangeGroupChannelConfig]
    const [open, setOpen] = useState(false)
    const reference = useRef(null)

    const toggleOpen = (): void => {
        reference.current.classList.remove('selectedChannelOpen')
        setOpen(s => !s)
    }

    const isEditor = React.useContext<{ editor: string }>(WidgetEditorContext)?.editor

    const changeChannelConfig = (configKey: string, newValue: string | boolean | AlarmsProps): void => {
        if (typeof newValue === 'string' || typeof newValue === 'boolean') {
            onChangeGroupChannelConfig(channelOption.mnemonic, configKey, newValue)
        } else {
            onChangeGroupChannelConfig(channelOption.mnemonic, 'alarms', newValue)
        }
    }

    return (
        <SelectedChannelOptionContainer
            data-testid={`${channelOption.value}-config-container`}
            open={open}
            onSubmit={e => e.preventDefault()}
            isEditor={isEditor}
            channelColor={channelOption.color}
        >
            <div ref={reference} className={` selectedChannel ${open ? 'selectedChannelOpen' : ''}`}>
                <header className={`selectedChannel--header`}>
                    <h3 className={`selectedChannel--header__heading`} onClick={toggleOpen}>
                        <button
                            className={`selectedChannel--header__heading--accordionBtn`}
                            aria-controls={channelOption.mnemonic}
                            aria-label={`${i18n('Open chart options for')} ${channelOption.label}`}
                            aria-expanded={open}
                        >
                            <span className={`selectedChannel--header__heading--accordionBtn__title`}>
                                {channelOption.label}
                            </span>

                            <i
                                role="channel-config-toggler"
                                className={`fas color-primary fa-angle-${
                                    open ? 'up' : 'down'
                                } selectedChannel--header__heading--accordionBtn__chevron`}
                            />
                        </button>
                    </h3>
                    <button
                        onClick={() => onRemove(channelOption)}
                        className={`selectedChannel--header__removeBtn`}
                        title={`${i18n('Remove')} ${channelOption.label} ${i18n('channel')}`}
                    >
                        <i className={` far fa-times-circle`} />
                    </button>
                </header>
                <div className={`selectedChannel--channelConfigs`}>
                    {open ? (
                        <ChannelConfigs
                            onChangeGroup={props.onChangeGroup}
                            channelOption={channelOption}
                            onChange={changeChannelConfig}
                            isEditor={Boolean(isEditor)}
                        ></ChannelConfigs>
                    ) : null}
                </div>
            </div>
        </SelectedChannelOptionContainer>
    )
}

interface IContainerProps {
    open: boolean
    isEditor?: string
    channelColor: string
}
const SelectedChannelOptionContainer = styled.form<IContainerProps>`
    --buttonHeight: 32px;
    .fa-chart-bar {
        font-size: 15px;
    }

    .selectedChannel {
        overflow: visible;
        max-height: var(--buttonHeight);
        border-radius: 5px;
        
        .input-field{
        }

        &--channelConfigs{
        }
        
        &.selectedChannelOpen {
            max-height: 100%;
        }
        
        &--header {
            border-radius: ${({ open }) => (!open ? '3px' : 'unset')};
            border-top-left-radius: 3px;
            border-top-right-radius: 3px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            height: var(--buttonHeight);
            background: ${({ isEditor }) => (isEditor ? 'var(--gray-300)' : 'var(--static-gray-900)')};
            &__heading {
                flex: 1;
                &--accordionBtn {
                    padding: 7px;
                    border: none;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    width: 100%;
                    color: ${({ theme }) => theme['link-color']};
                    background: none;
                    &__picked-color {
                        width: 12px;
                        height: 12px;
                        border-radius: 0px;
                        background: ${({ channelColor }) => channelColor};
                    }
                    &__title {
                        flex: 1;
                        margin-left: 7px;
                        color: ${({ theme, isEditor }) => (isEditor ? theme['main-tipography'] : 'white')};
                        font-size: 13px;
                        line-height: normal;
                        text-align: left;
                    }
                    &__chevron {
                        font-size: 14px;
                        margin-left: 10px;
                        cursor: pointer;
                    }
                }
            }
            &__removeBtn {
                padding: 7px 7px 7px 0;
                border: none;
                background: none;
            }
        }
    }
`
